import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { searchApi } from '@/services/search.api';
import { ApiError } from '@/services/api-client';
import { Alert, EmptyState, Spinner } from '@/components/ui';

/** Where a result of each module leads. Modules with no detail page render as plain text. */
const RESULT_LINK: Record<string, (resourceId: string) => string> = {
  PEOPLE: (id) => `/people/${id}`,
  GROUPS: (id) => `/groups/${id}`,
  EVENTS: (id) => `/events/${id}`,
};

export function SearchResultsPage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';

  const query = useQuery({
    queryKey: ['search', q],
    queryFn: ({ signal }) => searchApi.query(q, signal),
    enabled: q.trim().length >= 2,
  });

  const results = query.data ?? [];

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">{t('search.title', { values: { query: q } })}</h1>
      </header>

      {q.trim().length < 2 && <Alert tone="warning">{t('search.tooShort')}</Alert>}

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && q.trim().length >= 2 && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && results.length === 0 && <EmptyState title={t('search.empty')} />}

      {results.length > 0 && (
        <ul className="field-list">
          {results.map((result) => {
            const toHref = RESULT_LINK[result.moduleKey];
            return (
              <li key={`${result.resourceType}-${result.resourceId}`}>
                {toHref ? (
                  <Link to={toHref(result.resourceId)} className="person-name">
                    {result.title}
                  </Link>
                ) : (
                  <span>{result.title}</span>
                )}
                <span className="cell-empty"> · {t(`search.moduleKey.${result.moduleKey}`)}</span>
                {result.subtitle && <span className="cell-empty"> · {result.subtitle}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
